




































































/* eslint-disable @typescript-eslint/camelcase */
import { Component, Mixins, Watch } from 'vue-property-decorator'
import { namespace, Getter, Action } from 'vuex-class'
import { Cid, DraftPost, Post } from 'types'
import { ComposerModalAlertData } from 'vuexstore/modules/composer_modal_alert'
import { isNew, isNonEmpty } from 'vuexstore/helpers/post'
import window from 'bits/global'
import { ALERT_ICON } from 'bits/confirmation_dialog'
import OzBaseButton from 'library/v4/components/OzBaseButton.vue'
import { OzConfirmationDialogBoxButtonScheme } from 'library/v4/components/OzConfirmationDialogBox.vue'

const draft = namespace('draft')
const snackbars = namespace('snackbars')
const composerModal = namespace('composerModal')
const composerModalAlert = namespace('composerModalAlert')
const confirmationDialog = namespace('confirmationDialog')
const dragAndDropEvents = namespace('dragAndDropEvents')
const postModule = namespace('post')
const table = namespace('table')
const browser = namespace('browser')

@Component({
  components: {
    SurfaceComposerModalNew: (): any => import('vuecomponents/surface-composer-modal-new.vue'),
    OzBaseButton: (): any => import('library/v4/components/OzBaseButton.vue'),
    OzIcon: (): any => import('library/v4/components/OzIcon.vue'),
    SurfaceDraftBulkActionPopover: (): any => import('vuecomponents/SurfaceDraftBulkActionPopover.vue'),
  },
})
export default class SurfacePanelComposerModal extends Mixins() {
  $refs!: {
    bulkActionButton: OzBaseButton
  }

  @Getter uploadLimit!: number
  @Getter isSectioned!: boolean
  @Getter isTable!: boolean
  @Getter isUsingNewSurfaceShadows!: boolean
  @draft.Getter activeDraft!: DraftPost
  @draft.Getter draftCids!: Cid[]
  @draft.Getter draftByCid!: (cid: Cid) => DraftPost | undefined
  @draft.Getter isCidActiveDraft!: (cid: Cid) => boolean
  @draft.Getter isAnyDraftActive!: boolean
  @draft.Getter activeDraftUploadingFile!: File | null | undefined
  @postModule.Getter pendingPosts!: Post[]
  @postModule.Getter pendingPostEntitiesByCid!: Record<Cid, Post>
  @postModule.Getter getPostByCid!: (cid: Cid) => Post | undefined
  @table.Getter('postsPendingUpload') tablePostsToUpload!: Post[]
  @composerModal.Getter('isExpanded') isComposerModalExpandedWhenOpened!: boolean
  @dragAndDropEvents.Getter('isHandlingDragEvent') isPageHandlingDragEvent!: boolean
  @browser.Getter windowWidth!: number
  @browser.Getter windowHeight!: number

  @Action showOverFileSizeModal!: ({ file }: { file: File }) => void
  @snackbars.Action setSnackbar!: (payload: { message: string }) => void
  @draft.Action stopUploadFileInDraft!: (payload: { cid: Cid }) => void
  @draft.Action updateDraft!: (post: DraftPost) => void
  @draft.Action createNewDraftFromPendingUpload!: (payload: { file: File; postAttributes: Post }) => void
  @draft.Action stopAutoPublishDraft!: (payload: { cid: Cid }) => void
  @composerModalAlert.Action openAlert!: (data: ComposerModalAlertData) => void
  @postModule.Action removePendingPosts!: (payload: { cids: Cid[] }) => void
  @postModule.Action savePost!: ({ attributes }: { attributes: Post | DraftPost }) => void
  @composerModal.Action('expand') expandComposerModal!: () => void

  get pendingPostReadyToUploadCids(): Cid[] {
    // Logic copied from the "uploads" computed value in surface_add_post.vue
    // if there are posts for which sections are yet to be picked,
    // don't start uploading them. filter them out

    if (this.isTable) {
      return this.tablePostsToUpload.filter((post) => !!post.file).map((post) => post.cid)
    }

    const pendingPostsWithFile = this.pendingPosts.filter((post) => !!post.file)
    if (this.isSectioned) {
      const pendingPostsWithSections = pendingPostsWithFile.filter((x) => !!x.wall_section_id)
      return pendingPostsWithSections.map((post) => post.cid)
    }

    return pendingPostsWithFile.map((post) => post.cid)
  }

  get isComposerModalExpanded(): boolean {
    return this.isAnyDraftActive && this.isComposerModalExpandedWhenOpened
  }

  @Watch('pendingPostReadyToUploadCids')
  respondToNewPendingPostsReadyToUpload(pendingUploadPostCids: Cid[]): void {
    if (pendingUploadPostCids.length !== 0) {
      pendingUploadPostCids.forEach((cid) => {
        const pendingPostAttributes = this.pendingPostEntitiesByCid[cid]
        if (pendingPostAttributes.file) {
          this.createNewDraftFromPendingUpload({
            file: pendingPostAttributes.file,
            postAttributes: pendingPostAttributes,
          })
        }
      })
      this.removePendingPosts({ cids: pendingUploadPostCids })
    }
  }

  @Watch('activeDraft')
  respondToActiveDraftChanges(newActiveDraft: DraftPost | null): void {
    // Expand the composer modal if we edit an existing post
    if (newActiveDraft && !isNew(newActiveDraft)) {
      this.expandComposerModal()
    }
  }

  @Watch('windowWidth')
  @Watch('windowHeight')
  respondToWindowSizeChanges(): void {
    this.updateBulkActionPopoverAnchor()
  }

  hideMarginStyle(draftCid: Cid): Record<string, string> {
    // Note: must hide margin with style, tailwindcss class will be overidden by the container's space-x
    // Tailwindcss space-x adds unnecessary margin in some cases
    if (this.draftCids.length < 2) return {}

    // When a draft is expanded, it's still under the flex container so space-x adds extra right margin to it
    if (this.isCidActiveDraft(draftCid) && this.isComposerModalExpandedWhenOpened) {
      return { marginRight: '0px' }
    }
    return {}
  }

  // for upload file mixin used in SurfaceComposerModalNew START
  uploadingFileByCid(cid: Cid): File | null | undefined {
    return this.draftByCid(cid)?.uploadingFile
  }

  maxFileUploadSizeByCid(cid: Cid): number {
    return this.draftByCid(cid)?.maxFileUploadSize || this.uploadLimit
  }

  onUploadError(file: File, message: string, draftCid: Cid): void {
    this.stopUploadFileInDraft({ cid: draftCid })
    this.stopAutoPublishDraft({ cid: draftCid })
    if (this.isCidActiveDraft(draftCid)) {
      this.openAlert({
        title: this.__('There was an error loading the attachment'),
        body: message,
        primaryActionText: this.__('Try again'),
        secondaryActionText: this.__('Cancel'),
        primaryActions: [
          {
            type: 'draft/uploadFileActiveDraft',
            payload: { file, maxFileUploadSize: this.draftByCid(draftCid)?.maxFileUploadSize },
          },
        ],
        secondaryActions: [],
      })
    } else {
      this.setSnackbar({ message })
    }
  }

  onUploadCancel(file: File, draftCid: Cid): void {
    this.stopAutoPublishDraft({ cid: draftCid })
    // uploadingFile may have already been set to null if the user click on the trash icon in the upload hex grid
    if (this.draftByCid(draftCid)?.uploadingFile) {
      this.stopUploadFileInDraft({ cid: draftCid })
    }
  }

  onUploadReject(file: File, reason: Record<string, any>, draftCid: Cid): void {
    this.stopUploadFileInDraft({ cid: draftCid })
    this.stopAutoPublishDraft({ cid: draftCid })
    // For now the only reason for an upload to be rejected is size
    this.showOverFileSizeModal({ file })
  }

  onUploadDone(file: File, url: string, draftCid: Cid): void {
    this.stopUploadFileInDraft({ cid: draftCid })
    this.updateDraft({
      cid: draftCid,
      attachment: url,
      wish_content: {
        attachment_props: {
          url: url,
        },
        is_processed: false,
      },
    })
    if (this.draftByCid(draftCid)?.shouldAutoPublish) {
      this.savePost({ attributes: this.draftByCid(draftCid) as DraftPost })
    }
  }
  // for upload file mixin used in SurfaceComposerModalNew END

  checkUnsavedDraft(event: BeforeUnloadEvent): string | undefined {
    // check if there are any unsaved draft and warn the user when the browser tab is closed
    // Note: this is run using beforeunload which will also fire when a form is submitted. This doesn't affect anything for now
    const hasUnsavedDraft = this.draftCids.some((draftCid: Cid) => {
      const draft = this.draftByCid(draftCid)

      if (!draft) {
        return false
      }
      const post = this.getPostByCid(draftCid)

      const isUnsavedNewNonEmptyDraft = isNew(draft) && isNonEmpty(draft)
      const isDraftWithFileUpload = !!draft.uploadingFile
      const isDraftChangedFromOriginalPost =
        draft.subject !== post?.subject ||
        draft.body !== post?.body ||
        draft.attachment !== post?.attachment ||
        draft.attachment_caption !== post?.attachment_caption

      return isUnsavedNewNonEmptyDraft || isDraftWithFileUpload || isDraftChangedFromOriginalPost
    })

    if (!hasUnsavedDraft) {
      return undefined
    }

    const unsavedDraftMessage = this.__(
      'It looks like you have unsaved changes. Are you sure you want to discard them and close this page?',
    )

    // set the alert, reference: https://stackoverflow.com/a/7317311/13700092
    // most browsers don't support custom message any more, usually a generic message will be shown

    // For the alert to show in Gecko + IE
    event.returnValue = unsavedDraftMessage

    // For the alert to show in Gecko + Webkit, Safari, Chrome, ...
    return unsavedDraftMessage
  }

  /**
   * BULK ACTION MENU
   */
  xBulkActionMenu = false
  bulkActionPopoverAnchor = {}

  @draft.Action publishDraft!: (payload: { cid: Cid }) => void
  @confirmationDialog.Action openConfirmationDialog!: (params: unknown) => void

  openBulkActionMenu(): void {
    this.updateBulkActionPopoverAnchor()
    this.xBulkActionMenu = true
  }

  closeBulkActionMenu(): void {
    this.xBulkActionMenu = false
  }

  updateBulkActionPopoverAnchor(): void {
    const bulkActionButton = this.$refs.bulkActionButton
    if (!bulkActionButton) {
      this.bulkActionPopoverAnchor = {}
      return
    }

    const bulkActionButtonRect = bulkActionButton.$el.getBoundingClientRect()

    this.bulkActionPopoverAnchor = {
      top: bulkActionButtonRect.top,
      left: bulkActionButtonRect.left - 18,
      width: bulkActionButtonRect.width + 32,
    }
  }

  publishAllDrafts(): void {
    this.closeBulkActionMenu()
    // reverse to publish the oldest draft first
    // need to reverse on a separate line: https://sonarcloud.io/organizations/padlet/rules?open=typescript%3AS4043&rule_key=typescript%3AS4043
    const reversedDraftCids = [...this.draftCids].reverse()
    reversedDraftCids.forEach((cid) => {
      this.publishDraft({ cid })
    })
  }

  deleteAllDrafts(): void {
    this.closeBulkActionMenu()
    this.openConfirmationDialog({
      ...ALERT_ICON,
      isCodeProtected: false,
      title: this.__('Delete all drafts?'),
      body: this.__("You can't undo this action."),
      confirmButtonText: this.__('Delete drafts'),
      cancelButtonText: this.__('Cancel'),
      afterConfirmActions: [{ type: 'draft/deleteAllDrafts' }],
      buttonScheme: OzConfirmationDialogBoxButtonScheme.Danger,
      forceFullWidthButtons: true,
      xShadow: this.isUsingNewSurfaceShadows,
    })
  }

  /**
   * LIFE CYCLES
   */

  mounted(): void {
    window.addEventListener('beforeunload', this.checkUnsavedDraft)
  }

  beforeDestroy(): void {
    window.removeEventListener('beforeunload', this.checkUnsavedDraft)
  }
}
